// Calls the tour → 3D service through the avatar backend (/api/convert3d → the isolated convert3d service on
// avatar-prod, utils/convert3d/). Like "Detect room" it always runs on prod; `?layoutApi=<origin>` overrides.
// Results are kept per environment + the lowest scene ID among the plan's rooms ("dev4-18622"): the admin and the
// published page both have the plan, but the published page has no tour ID. Scene IDs are unique per environment.
// No DB column is needed.
import { layoutApiBase, absolutePanoramaUrl } from '../floorplan-detect/api';

export function environment(hostname = window.location.hostname) {
  if (/(^|\.)dev4\./.test(hostname)) return 'dev4';
  if (/(^|\.)test\./.test(hostname)) return 'test';
  return 'prod';
}

export function jobKey(plan, hostname) {
  const ids = buildableRooms(plan).map(scene => String(scene.id)).filter(id => /^\d{1,9}$/.test(id)).map(Number);
  return ids.length ? `${environment(hostname)}-${Math.min(...ids)}` : null;
}

// Rooms the service can build: a scene panorama and at least 3 corners.
export function buildableRooms(plan) {
  const rooms = [];
  Object.values((plan && plan.layers) || {}).forEach(layer => {
    Object.values(layer.areas || {}).forEach(area => {
      const scene = (area.properties && area.properties.scene) || {};
      if (scene.panorama && (area.vertices || []).length >= 3) rooms.push(scene);
    });
  });
  return rooms;
}

// Copy of the plan with absolute panorama URLs (some tours store them relative to the tour domain), in the main
// layers and in `preview` (the joined-up layout the converter places rooms by).
export function planForService(plan, pageUrl = window.location.href) {
  const copy = JSON.parse(JSON.stringify(plan));
  const layers = [...Object.values(copy.layers || {}), ...Object.values((copy.preview && copy.preview.layers) || {})];
  layers.forEach(layer => {
    Object.values(layer.areas || {}).forEach(area => {
      const scene = area.properties && area.properties.scene;
      if (scene && scene.panorama) scene.panorama = absolutePanoramaUrl(scene.panorama, pageUrl) || '';
    });
  });
  return copy;
}

function errorText(data, status) {
  const d = data && (data.detail || data.error);
  if (Array.isArray(d)) return d.map(x => (x && x.msg) || String(x)).join('; ');
  return (typeof d === 'string' && d) || `3D service request failed (${status})`;
}

async function call(path, options, fetchFn) {
  const res = await fetchFn(`${layoutApiBase()}${path}`, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(errorText(data, res.status));
  return data;
}

export function startBuild(key, plan, { fetchFn = fetch } = {}) {
  return call('/api/convert3d', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, plan }),
  }, fetchFn);
}

export function getStatus(key, { fetchFn = fetch } = {}) {
  return call(`/api/convert3d/${key}`, {}, fetchFn);
}

export function fileUrl(key, name, version = '') {
  return `${layoutApiBase()}/api/convert3d/${key}/files/${name}${version ? `?v=${version}` : ''}`;
}

export function hasWorlds(s) {
  return !!(s && s.report && (s.report.rooms || []).some(r => r.world && r.world.spz));
}

// The 3D Marble page (data/convert3d/world.html) lives on the admin host of each environment (data/ is the web
// root); published tours run on vr-360-tour.com domains, so they load it from there.
export function adminOrigin(hostname = window.location.hostname) {
  return { dev4: 'https://dev4.e3.eyespy360.com', test: 'https://test.e3.eyespy360.com' }[environment(hostname)]
    || 'https://e3.eyespy360.com';
}

export function marbleUrl(key, origin = window.location.origin) {
  return `${origin}/convert3d/world.html?key=${encodeURIComponent(key)}`;
}
